import { mockReviewSeeds } from '@/mocks/reviews';
import type { Driver, RatingSummary, Review } from '@/types';
import { fetchDrivers } from './driversService';
import { delay, shouldSimulateError } from './simulation';

/** Reviews whose targetTag equals this value belong to the laundry itself. */
export const LAUNDRY_TARGET = 'laundry';

/** Build an ISO date that is always in the past from a "minutes ago" seed. */
function fromMinutesAgo(minutesAgo: number): string {
  return new Date(Date.now() - minutesAgo * 60000).toISOString();
}

/** Resolve the demo seeds into reviews with real, always-past dates. */
const mockReviews: Review[] = mockReviewSeeds.map((seed) => ({
  id: seed.id,
  targetId: seed.targetId,
  customerName: seed.customerName,
  rating: seed.rating,
  comment: seed.comment,
  createdAt: fromMinutesAgo(seed.createdMinutesAgo),
  orderId: seed.orderId,
}));

/** Round to one decimal so averages read cleanly in the UI. */
function averageOf(reviews: Review[]): number | null {
  if (reviews.length === 0) return null;
  const sum = reviews.reduce((total, review) => total + review.rating, 0);
  return Math.round((sum / reviews.length) * 10) / 10;
}

/** Summary is always computed from the given (full) set, never a fixed value. */
export function summarize(reviews: Review[]): RatingSummary {
  return { average: averageOf(reviews), count: reviews.length };
}

function byNewest(a: Review, b: Review): number {
  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
}

function sortNewest(reviews: Review[]): Review[] {
  return [...reviews].sort(byNewest);
}

export interface LaundryReviewsResult {
  summary: RatingSummary;
  reviews: Review[];
}

/** The laundry's own reviews, newest first, with the overall summary. */
export async function fetchLaundryReviews(): Promise<LaundryReviewsResult> {
  await delay();
  if (shouldSimulateError('reviews')) throw new Error('failed-to-load-reviews');
  const all = mockReviews.filter((review) => review.targetId === LAUNDRY_TARGET);
  return { summary: summarize(all), reviews: sortNewest(all) };
}

export interface DriverRatingRow {
  driver: Driver;
  summary: RatingSummary;
}

/**
 * Every sub-driver of the laundry joined with the rating summary of their
 * reviews. Uses the same driver dataset as the sub-drivers page, so a
 * suspended account keeps its review history.
 */
export async function fetchDriversRatings(): Promise<DriverRatingRow[]> {
  await delay();
  if (shouldSimulateError('reviews')) throw new Error('failed-to-load-reviews');
  const drivers = await fetchDrivers();
  return drivers.map((driver) => ({
    driver,
    summary: summarize(mockReviews.filter((review) => review.targetId === driver.id)),
  }));
}

export interface DriverReviewsResult {
  driver: Driver;
  summary: RatingSummary;
  reviews: Review[];
}

/** The reviews of one specific sub-driver, newest first. */
export async function fetchDriverReviews(driverId: string): Promise<DriverReviewsResult> {
  await delay(500);
  if (shouldSimulateError('reviews')) throw new Error('failed-to-load-reviews');
  const drivers = await fetchDrivers();
  const driver = drivers.find((item) => item.id === driverId);
  if (!driver) throw new Error('driver-not-found');
  const reviews = mockReviews.filter((review) => review.targetId === driverId);
  return { driver, summary: summarize(reviews), reviews: sortNewest(reviews) };
}